import { appIntl } from '@nxsys/helpers/Common';
import { PaginationType } from '@nxsys/types/models/Common';
import { IPayroll } from '@nxsys/types/models/payroll';
import { showToast } from '@nxsys/utils/toast';
import { createSlice } from '@reduxjs/toolkit';
import { dashboardActs } from '../actions';
import {
  GetAgencyPaymentListParams,
  GetAssignmentDashboardListParams,
  GetDashboardPaymentEPSListParams,
  GetDashboardPaymentListParams,
  GetEmployeeAssignmentListParams,
  GetEmployeeAwaitingPaymentListParams,
  GetEmployeeDashboardListParams,
  GetEmployeeDocumentNearingExpiryListParams,
  GetEmployeeTimesheetListParams,
  GetExpenseListParams,
  GetInvoicingListParams,
  GetLeaverFPSListParams,
  GetOnHoldListParams,
  GetPaymentFileListParams,
  RequiredAlertsCounts,
} from '../types';
import { IPresubmitEPS } from '@nxsys/types/models/presubmitEPS';
import { LeaverFPSRecordType } from '@nxsys/types/models/RTI';
import { IExpense } from '@nxsys/types/models/expense';
import { Invoice } from '@nxsys/types/models/invoice';
import { TimeSheetDataItem } from '@nxsys/types/models/timesheet';
import { EmployeeRecordType } from '@nxsys/types/models/employee';
import { Assignment } from '../../assignment/types';
import { GetAgencyTimesheetListParams } from '../../agency/types';
import { Payroll } from '../../revenueFilings/types';
import { DocumentType } from '@nxsys/types/models/document';

export type DashboardState = {
  paymentList: {
    loading: boolean;
    list: IPayroll[];
    pagination: PaginationType;
    queryParams: GetDashboardPaymentListParams | null;
  };
  epsList: {
    loading: boolean;
    list: IPresubmitEPS[];
    pagination: PaginationType;
    queryParams: GetDashboardPaymentEPSListParams | null;
  };
  leaverFPSList: {
    loading: boolean;
    list: LeaverFPSRecordType[];
    pagination: PaginationType;
    queryParams: GetLeaverFPSListParams | null;
  };
  expenseList: {
    loading: boolean;
    list: IExpense[];
    pagination: PaginationType;
    queryParams: GetExpenseListParams | null;
  };
  invoicingList: {
    loading: boolean;
    list: Invoice[];
    pagination: PaginationType;
    queryParams: GetInvoicingListParams | null;
  };
  onHoldList: {
    loading: boolean;
    list: TimeSheetDataItem[];
    pagination: PaginationType;
    queryParams: GetOnHoldListParams | null;
  };
  paymentFileList: {
    loading: boolean;
    list: IPayroll[];
    pagination: PaginationType;
    queryParams: GetPaymentFileListParams | null;
  };
  employeeList: {
    loading: boolean;
    list: EmployeeRecordType[];
    pagination: PaginationType;
    queryParams: GetEmployeeDashboardListParams | null;
  };
  assignmentList: {
    loading: boolean;
    list: Assignment[];
    pagination: PaginationType;
    queryParams: GetAssignmentDashboardListParams | null;
  };
  agencyTimesheetList: {
    loading: boolean;
    list: TimeSheetDataItem[];
    pagination: PaginationType;
    queryParams: GetAgencyTimesheetListParams | null;
  };
  agencyPaymentList: {
    loading: boolean;
    list: Invoice[];
    pagination: PaginationType;
    queryParams: GetAgencyPaymentListParams | null;
  };
  employeeAwaitingPaymentList: {
    loading: boolean;
    list: Payroll[];
    pagination: PaginationType;
    queryParams: GetEmployeeAwaitingPaymentListParams | null;
  };
  employeeDocumentNearingExpiryList: {
    loading: boolean;
    list: DocumentType[];
    pagination: PaginationType;
    queryParams: GetEmployeeDocumentNearingExpiryListParams | null;
  };
  employeeTimesheetList: {
    loading: boolean;
    list: TimeSheetDataItem[];
    pagination: PaginationType;
    queryParams: GetEmployeeTimesheetListParams | null;
  };
  employeeAssignmentList: {
    loading: boolean;
    list: Assignment[];
    pagination: PaginationType;
    queryParams: GetEmployeeAssignmentListParams | null;
  };
  requiredAlertsData: {
    data: RequiredAlertsCounts | null;
    loading: boolean;
  };
};

const initialState: DashboardState = {
  paymentList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  epsList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  leaverFPSList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  expenseList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  invoicingList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  onHoldList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  paymentFileList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  employeeList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  assignmentList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  agencyTimesheetList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  agencyPaymentList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  employeeAwaitingPaymentList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  employeeDocumentNearingExpiryList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  employeeTimesheetList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  employeeAssignmentList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  requiredAlertsData: {
    data: null,
    loading: false,
  },
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Get Payment List Records
    builder.addCase(dashboardActs.getDashboardPaymentList.pending, (state) => {
      state.paymentList.loading = true;
    });
    builder.addCase(
      dashboardActs.getDashboardPaymentList.fulfilled,
      (state, action) => {
        state.paymentList.loading = false;
        state.paymentList.list = action.payload.data;
        state.paymentList.pagination = action.payload.pagination;
        state.paymentList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(dashboardActs.getDashboardPaymentList.rejected, (state) => {
      state.paymentList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'dashboard.paymentList.getError',
        }),
      });
    });

    // Get Payment EPS List Records
    builder.addCase(
      dashboardActs.getDashboardPaymentEPSList.pending,
      (state) => {
        state.epsList.loading = true;
      },
    );
    builder.addCase(
      dashboardActs.getDashboardPaymentEPSList.fulfilled,
      (state, action) => {
        state.epsList.loading = false;
        state.epsList.list = action.payload.data;
        state.epsList.pagination = action.payload.pagination;
        state.epsList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(
      dashboardActs.getDashboardPaymentEPSList.rejected,
      (state) => {
        state.epsList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'dashboard.epsList.getError',
          }),
        });
      },
    );

    // Get Leaver FPS List Records
    builder.addCase(dashboardActs.getLeaverFPSList.pending, (state) => {
      state.leaverFPSList.loading = true;
    });
    builder.addCase(
      dashboardActs.getLeaverFPSList.fulfilled,
      (state, action) => {
        state.leaverFPSList.loading = false;
        state.leaverFPSList.list = action.payload.data;
        state.leaverFPSList.pagination = action.payload.pagination;
        state.leaverFPSList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(dashboardActs.getLeaverFPSList.rejected, (state) => {
      state.leaverFPSList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'dashboard.leaverFPSList.getError',
        }),
      });
    });

    // Get Expense List Records
    builder.addCase(dashboardActs.getExpenseList.pending, (state) => {
      state.expenseList.loading = true;
    });
    builder.addCase(dashboardActs.getExpenseList.fulfilled, (state, action) => {
      state.expenseList.loading = false;
      state.expenseList.list = action.payload.data;
      state.expenseList.pagination = action.payload.pagination;
      state.expenseList.queryParams = (action.payload as any).queryParams;
    });
    builder.addCase(dashboardActs.getExpenseList.rejected, (state) => {
      state.expenseList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'dashboard.expenseList.getError',
        }),
      });
    });

    // Get Invoicing List Records
    builder.addCase(dashboardActs.getInvoicingList.pending, (state) => {
      state.invoicingList.loading = true;
    });
    builder.addCase(
      dashboardActs.getInvoicingList.fulfilled,
      (state, action) => {
        state.invoicingList.loading = false;
        state.invoicingList.list = action.payload.data;
        state.invoicingList.pagination = action.payload.pagination;
        state.invoicingList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(dashboardActs.getInvoicingList.rejected, (state) => {
      state.invoicingList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'dashboard.invoicingList.getError',
        }),
      });
    });

    // Get On Hold List Records
    builder.addCase(dashboardActs.getOnHoldList.pending, (state) => {
      state.onHoldList.loading = true;
    });
    builder.addCase(dashboardActs.getOnHoldList.fulfilled, (state, action) => {
      state.onHoldList.loading = false;
      state.onHoldList.list = action.payload.data;
      state.onHoldList.pagination = action.payload.pagination;
      state.onHoldList.queryParams = (action.payload as any).queryParams;
    });
    builder.addCase(dashboardActs.getOnHoldList.rejected, (state) => {
      state.onHoldList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'dashboard.onHoldList.getError',
        }),
      });
    });

    // Get Payment File List Records
    builder.addCase(dashboardActs.getPaymentFileList.pending, (state) => {
      state.paymentFileList.loading = true;
    });
    builder.addCase(
      dashboardActs.getPaymentFileList.fulfilled,
      (state, action) => {
        state.paymentFileList.loading = false;
        state.paymentFileList.list = action.payload.data;
        state.paymentFileList.pagination = action.payload.pagination;
        state.paymentFileList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(dashboardActs.getPaymentFileList.rejected, (state) => {
      state.paymentFileList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'dashboard.paymentFileList.getError',
        }),
      });
    });

    // Get Employee List Records
    builder.addCase(dashboardActs.getEmployeeDashboardList.pending, (state) => {
      state.employeeList.loading = true;
    });
    builder.addCase(
      dashboardActs.getEmployeeDashboardList.fulfilled,
      (state, action) => {
        state.employeeList.loading = false;
        state.employeeList.list = action.payload.data;
        state.employeeList.pagination = action.payload.pagination;
        state.employeeList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(
      dashboardActs.getEmployeeDashboardList.rejected,
      (state) => {
        state.employeeList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'dashboard.employeeList.getError',
          }),
        });
      },
    );

    // Get Assignment List Records
    builder.addCase(
      dashboardActs.getAssignmentDashboardList.pending,
      (state) => {
        state.assignmentList.loading = true;
      },
    );
    builder.addCase(
      dashboardActs.getAssignmentDashboardList.fulfilled,
      (state, action) => {
        state.assignmentList.loading = false;
        state.assignmentList.list = action.payload.data;
        state.assignmentList.pagination = action.payload.pagination;
        state.assignmentList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(
      dashboardActs.getAssignmentDashboardList.rejected,
      (state) => {
        state.assignmentList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'dashboard.assignmentList.getError',
          }),
        });
      },
    );

    // Get Placement List Records
    builder.addCase(
      dashboardActs.getPlacementDashboardList.pending,
      (state) => {
        state.assignmentList.loading = true;
      },
    );
    builder.addCase(
      dashboardActs.getPlacementDashboardList.fulfilled,
      (state, action) => {
        state.assignmentList.loading = false;
        state.assignmentList.list = action.payload.data;
        state.assignmentList.pagination = action.payload.pagination;
        state.assignmentList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(
      dashboardActs.getPlacementDashboardList.rejected,
      (state) => {
        state.assignmentList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'dashboard.placementList.getError',
          }),
        });
      },
    );

    // Get Agency Timesheet List Records
    builder.addCase(dashboardActs.getAgencyTimesheetList.pending, (state) => {
      state.agencyTimesheetList.loading = true;
    });
    builder.addCase(
      dashboardActs.getAgencyTimesheetList.fulfilled,
      (state, action) => {
        state.agencyTimesheetList.loading = false;
        state.agencyTimesheetList.list = action.payload.data;
        state.agencyTimesheetList.pagination = action.payload.pagination;
        state.agencyTimesheetList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(dashboardActs.getAgencyTimesheetList.rejected, (state) => {
      state.agencyTimesheetList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'dashboard.timesheetList.getError',
        }),
      });
    });

    // Get Agency Employee List Records
    builder.addCase(dashboardActs.getAgencyEmployeeDashboardList.pending, (state) => {
      state.employeeList.loading = true;
    });
    builder.addCase(
      dashboardActs.getAgencyEmployeeDashboardList.fulfilled,
      (state, action) => {
        state.employeeList.loading = false;
        state.employeeList.list = action.payload.data;
        state.employeeList.pagination = action.payload.pagination;
        state.employeeList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(
      dashboardActs.getAgencyEmployeeDashboardList.rejected,
      (state) => {
        state.employeeList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'dashboard.employeeList.getError',
          }),
        });
      },
    );

    // Get Agency Payment List Records
    builder.addCase(dashboardActs.getAgencyPaymentList.pending, (state) => {
      state.agencyPaymentList.loading = true;
    });
    builder.addCase(
      dashboardActs.getAgencyPaymentList.fulfilled,
      (state, action) => {
        state.agencyPaymentList.loading = false;
        state.agencyPaymentList.list = action.payload.data;
        state.agencyPaymentList.pagination = action.payload.pagination;
        state.agencyPaymentList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(
      dashboardActs.getAgencyPaymentList.rejected,
      (state) => {
        state.agencyPaymentList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'dashboard.paymentList.getError',
          }),
        });
      },
    );

    // Get Employee Awaiting Payment List Records
    builder.addCase(dashboardActs.getEmployeeAwaitingPaymentList.pending, (state) => {
      state.employeeAwaitingPaymentList.loading = true;
    });
    builder.addCase(
      dashboardActs.getEmployeeAwaitingPaymentList.fulfilled,
      (state, action) => {
        state.employeeAwaitingPaymentList.loading = false;
        state.employeeAwaitingPaymentList.list = action.payload.data;
        state.employeeAwaitingPaymentList.pagination = action.payload.pagination;
        state.employeeAwaitingPaymentList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(
      dashboardActs.getEmployeeAwaitingPaymentList.rejected,
      (state) => {
        state.employeeAwaitingPaymentList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'dashboard.paymentList.getError',
          }),
        });
      },
    );

    // Get Employee Document Nearing Expiry List Records
    builder.addCase(dashboardActs.getEmployeeDocumentNearingExpiryList.pending, (state) => {
      state.employeeDocumentNearingExpiryList.loading = true;
    });
    builder.addCase(
      dashboardActs.getEmployeeDocumentNearingExpiryList.fulfilled,
      (state, action) => {
        state.employeeDocumentNearingExpiryList.loading = false;
        state.employeeDocumentNearingExpiryList.list = action.payload.data;
        state.employeeDocumentNearingExpiryList.pagination = action.payload.pagination;
        state.employeeDocumentNearingExpiryList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(
      dashboardActs.getEmployeeDocumentNearingExpiryList.rejected,
      (state) => {
        state.employeeDocumentNearingExpiryList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'dashboard.documentList.getError',
          }),
        });
      },
    );

    // Get Employee Timesheet List Records
    builder.addCase(dashboardActs.getEmployeeTimesheetList.pending, (state) => {
      state.employeeTimesheetList.loading = true;
    });
    builder.addCase(
      dashboardActs.getEmployeeTimesheetList.fulfilled,
      (state, action) => {
        state.employeeTimesheetList.loading = false;
        state.employeeTimesheetList.list = action.payload.data;
        state.employeeTimesheetList.pagination = action.payload.pagination;
        state.employeeTimesheetList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(
      dashboardActs.getEmployeeTimesheetList.rejected,
      (state) => {
        state.employeeTimesheetList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'dashboard.timesheetList.getError',
          }),
        });
      },
    );

    // Get Employee Assignment List Records
    builder.addCase(dashboardActs.getEmployeeAssignmentList.pending, (state) => {
      state.employeeAssignmentList.loading = true;
    });
    builder.addCase(
      dashboardActs.getEmployeeAssignmentList.fulfilled,
      (state, action) => {
        state.employeeAssignmentList.loading = false;
        state.employeeAssignmentList.list = action.payload.data;
        state.employeeAssignmentList.pagination = action.payload.pagination;
        state.employeeAssignmentList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(
      dashboardActs.getEmployeeAssignmentList.rejected,
      (state) => {
        state.employeeAssignmentList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'dashboard.assignmentList.getError',
          }),
        });
      },
    );

    // Get Required Alerts
    builder.addCase(
      dashboardActs.getDashboardRequiredAlerts.pending,
      (state) => {
        state.requiredAlertsData.loading = true;
      },
    );
    builder.addCase(
      dashboardActs.getDashboardRequiredAlerts.fulfilled,
      (state, action) => {
        state.requiredAlertsData.loading = false;
        state.requiredAlertsData.data = action.payload?.data ?? null;
      },
    );
    builder.addCase(
      dashboardActs.getDashboardRequiredAlerts.rejected,
      (state) => {
        state.requiredAlertsData.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'dashboard.requiredAlerts.getError',
          }),
        });
      },
    );
  },
});

export const dashboardReducer = dashboardSlice.reducer;
export const dashboardActions = {
  ...dashboardSlice.actions,
  ...dashboardActs,
};
